import React, { Suspense } from 'react';
import { isPortalPath, startPortalEarly } from './portalPreload';
import { startPublicEarly } from './publicPreload';

/* Entry switch (Oct 2026).
   /biz is the franchise ad landing page. It must load ONLY the funnel — not the portals,
   CRM, ERP, attendance, login system or the other public pages — so it opens fast on mobile data.
   Every other address loads the full app from AppMain.js exactly as before. */

const isBizPath = () => {
    const p = (window.location.pathname || '/').replace(/\/+$/, '');
    return p === '/biz' || p.startsWith('/biz/');
};

// One-shot reload if a page file fails to download (e.g. right after a new deploy)
const lazyWithRetry = (factory) =>
    React.lazy(() =>
        factory().catch((err) => {
            const key = 'aro-chunk-reloaded';
            if (!sessionStorage.getItem(key)) {
                sessionStorage.setItem(key, '1');
                window.location.reload();
                return new Promise(() => {});
            }
            throw err;
        })
    );

const BizFunnel = lazyWithRetry(() => import('@/pages/public/biz/BizFunnel'));
const AppMain = lazyWithRetry(() => import('./AppMain'));

// On /biz, start fetching the offer settings while the page code downloads (saves a round trip).
if (isBizPath()) {
    try {
        window.__bizConfigPromise = fetch('/api/biz/config', { credentials: 'same-origin', cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));
        window.__bizConfigPromise.catch(() => {});
    } catch (e) { /* the page fetches normally if this fails */ }
}

// Staff portal opened directly: start the identity request and the portal's program files at once
// (performance batch 2; see portalPreload.js - nothing is shown before the identity check, as before).
if (!isBizPath() && isPortalPath(window.location.pathname)) {
    startPortalEarly(window.location.pathname);
}
// /service, Login and Register opened directly: start their code at once (performance batch 2; publicPreload.js).
if (!isBizPath()) {
    startPublicEarly(window.location.pathname);
}

const FALLBACK = (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontFamily: 'system-ui' }}>Loading…</div>
);

function App() {
    if (isBizPath()) {
        return (
            <Suspense fallback={<div style={{ minHeight: '100vh', background: '#050B1F' }} />}>
                <BizFunnel />
            </Suspense>
        );
    }
    return (
        <Suspense fallback={FALLBACK}>
            <AppMain />
        </Suspense>
    );
}

export default App;
